import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ensure, getById, getSortedCollection, getSortedObjectCollection } from "sprint-common-web";

import { ContentVersionDetails } from "../../../../common/components/content-version/ContentVersion";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { useAssociateContentFormContext } from "../context/useAssociateContentFormContext";
import {
    ContentAssociationTvSeasonDetails,
    ContentAssociationTvEpisodeDetails,
    ContentAssociationTvEpisodeVersionDetails,
    ensureTvAssociateDetails
} from "../types/ContentAssociationDetails";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";

interface ContentAssociationTvSelectionOpts {
    seasons: ContentAssociationTvSeasonDetails[];
    episodes: ContentAssociationTvEpisodeDetails[];
    versions: ContentAssociationTvEpisodeVersionDetails[];
}

export const useContentAssociationTvContentForm = () => {
    const { contentDetails, setValidationResult } = useAssociateContentFormContext();
    const { setValue, getValues, clearErrors, watch } = useFormContext<ContentAssociationFormState>();

    const [opts, setOpts] = useState<ContentAssociationTvSelectionOpts>({
        seasons: [],
        episodes: [],
        versions: []
    });

    const selectSeason = (seasonId: string) => {
        const season = getById(opts.seasons, seasonId);

        const sortedEpisodes = getSortedCollection(season.episodes, "number", "asc");

        setOpts({ ...opts, episodes: sortedEpisodes, versions: [] });

        setValue("episodeId", undefined);
        clearVersions();
    };

    const clearSeason = () => {
        setOpts({ ...opts, episodes: [], versions: [] });
        clearVersions();
    };

    const selectEpisode = (episodeId: string) => {
        const episode = getById(opts.episodes, episodeId);

        setOpts({ ...opts, versions: sortVersionsByType(filterVersions(episode.versions)) });
        clearVersions();
    };

    const clearEpisode = () => {
        setOpts({ ...opts, versions: [] });
        clearVersions();
    };

    const clearVersions = () => {
        setValue("versionIds", []);
        clearErrors("versionIds");
        setValidationResult(undefined);
    };

    const addNewVersion = (newVersion: ContentVersionDetails) => {
        const { season, episode } = addVersionToSeasonAndEpisode(newVersion);

        setOpts((prevOpts) => ({
            ...prevOpts,
            seasons: [...prevOpts.seasons, season],
            episodes: [...prevOpts.episodes, episode],
            versions: sortVersionsByType([newVersion, ...prevOpts.versions])
        }));

        const currentIds = getValues("versionIds") ?? [];

        if (shouldSelectNewVersion(currentIds, newVersion)) {
            setValue("versionIds", [...currentIds, newVersion.id]);
        }
    };

    const shouldSelectNewVersion = (selectedVersionIds: string[], newVersion: ContentVersionDetails) => {
        const selectedVersion = opts.versions.find((version) => selectedVersionIds.includes(version.id));

        if (!selectedVersion) return true;

        return (
            newVersion.type.id === selectedVersion.type.id &&
            newVersion.brandings?.length === selectedVersion.brandings?.length
        );
    };

    const addVersionToSeasonAndEpisode = (newVersion: ContentVersionDetails) => {
        const currentSeasonId = getValues("seasonId");
        const currentEpisodeId = getValues("episodeId");

        const season = opts.seasons.find((s) => s.id === currentSeasonId);

        ensure(season, "Season must be selected to add a new version");

        const episode = season.episodes.find((e) => e.id === currentEpisodeId);

        ensure(episode, "Episode must be selected to add a new version");

        const episodeVersions = [...episode?.versions, newVersion];

        season.episodes = [...season.episodes, { ...episode, versions: episodeVersions }];
        episode.versions = episodeVersions;

        return { season, episode };
    };

    const init = () => {
        const type = watch("contentType");

        if (!contentDetails || type !== ContentReferenceType.TvSeries) return;

        ensureTvAssociateDetails(contentDetails);

        const seasonId = getValues("seasonId");
        const episodeId = getValues("episodeId");
        const versionIds = getValues("versionIds");

        const initialOpts: ContentAssociationTvSelectionOpts = {
            seasons: contentDetails.seasons,
            episodes: [],
            versions: []
        };

        const season = seasonId ? contentDetails.seasons.find((s) => s.id === seasonId) : undefined;

        initialOpts.episodes = season?.episodes ?? [];

        if (episodeId) {
            const episode = getById(initialOpts.episodes, episodeId);
            initialOpts.versions = sortVersionsByType(filterVersions(episode.versions, versionIds));
        }

        setOpts(initialOpts);
    };

    return {
        init,

        opts,
        selections: {
            selectSeason,
            clearSeason,

            selectEpisode,
            clearEpisode
        },
        addNewVersion
    };
};

const filterVersions = (
    episodeVersions: ContentAssociationTvEpisodeVersionDetails[],
    selectedVersionIds?: string[]
): ContentAssociationTvEpisodeVersionDetails[] => {
    const versions = episodeVersions.filter((version) => !version.mergedTargetId && !version.buzzVersion?.isArchived);

    const filteredVersions = versions.filter(
        (version) => !isMergedVersion(version) && isAllowedByArchiveRule(version, selectedVersionIds)
    );

    return getSortedObjectCollection(filteredVersions, "asc", (listItem) => {
        return listItem.type.name;
    });
};

const sortVersionsByType = (arr: ContentAssociationTvEpisodeVersionDetails[]) =>
    arr.slice().sort((a, b) => a.type.id.localeCompare(b.type.id));

const isMergedVersion = (version: ContentAssociationTvEpisodeVersionDetails): boolean => !!version.mergedTargetId;

const isAllowedByArchiveRule = (
    version: ContentAssociationTvEpisodeVersionDetails,
    selectedVersionIds?: string[]
): boolean => !isArchivedVersion(version) || !!selectedVersionIds?.includes(version.id);

const isArchivedVersion = (version: ContentAssociationTvEpisodeVersionDetails): boolean =>
    !!version.buzzVersion?.isArchived;

const getSelectedVersionTypeId = (
    versionOptions: ContentAssociationTvEpisodeVersionDetails[],
    selectedVersionIds: string[]
): string | undefined => {
    return versionOptions.find((version) => selectedVersionIds.includes(version.id))?.type.id;
};
